import React from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { cn } from 'cn';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

import { capitalizeFirstLetter } from 'src/utils/string';
import Info from '../info';

type RadioOption = { id?: any; disabled?: boolean; [key: string]: any };

type RadioModel = {
  /** Título do grupo. */
  label?: React.ReactNode;
  /** A opção escolhida (com `needId`, só o id dela). */
  value?: unknown;
  /** Chamado com a opção escolhida (com `needId`, só o id). */
  requestChange: (value: unknown) => void;
  /** Deixa o grupo vermelho. */
  errors?: unknown;
  /** Mensagem de erro em vermelho abaixo das opções. */
  erros?: React.ReactNode;
};

type HintItem = { id: unknown; [key: string]: unknown };

type RadioProps = {
  /** Campo do formulário: `label`, `value`, `requestChange` e os erros. */
  model: RadioModel;
  /** As opções. Cada uma precisa de `id`; `disabled: true` desabilita só ela. */
  options?: RadioOption[];
  /** Campo da opção usado como texto (ex.: `'descricao'`). Sem ele nem `labelFunction`, a opção é o próprio texto. */
  labelField?: string;
  /** Monta o texto de cada opção. Tem prioridade sobre `labelField`. */
  labelFunction?: (option: RadioOption) => React.ReactNode;
  /** O `value` do model é só o id da opção, não o objeto. */
  needId?: boolean;
  /** Opções lado a lado. Com `false`, uma por linha. */
  inline?: boolean;
  /** Opções em duas colunas a partir de 768px. Use com o `inline` padrão: com `inline={false}`, fica uma coluna estreita. */
  inColum?: boolean;
  /** Com `inColum`, uma coluna só. */
  colunaUnica?: boolean;
  /** Mostra o título do `model`. */
  showLabel?: boolean;
  /** Põe ` *` no título. Não valida. */
  required?: boolean;
  disabled?: boolean;
  /** Nome do grupo no formulário. */
  name?: string;
  /** Ícone "i" ao lado das opções: lista com o `id` da opção e o texto em `hintLabel`. */
  hintItens?: HintItem[] | null;
  /** Campo de `hintItens` com o texto da ajuda. */
  hintLabel?: string;
  /** Sufixo do id dos ícones de ajuda, quando há mais de um grupo na tela. */
  hintKey?: string;
  /** Texto cinza abaixo de cada opção: lista com o `id` da opção e o texto em `label`. Deixa o título da opção em negrito. */
  descriptionItems?: { id: unknown; label?: string }[] | null;
  /** Espaço entre as opções, quando uma por linha (padrão 8px, ou 12px com `descriptionItems`). */
  optionSpacing?: string | null;
  /** Estilo do texto de cada opção. */
  optionLabelStyle?: React.CSSProperties;
  /** Chamado com a opção escolhida (sempre o objeto, mesmo com `needId`). */
  onChange?: (option: RadioOption) => void;
  /** Chamado depois de cada mudança, sem argumentos. */
  getAlteracao?: () => void;
  className?: string;
  style?: React.CSSProperties;
};

/**
 * Grupo de opções em que só uma pode ser escolhida, ligado a um campo do formulário (`model`). As setas do teclado navegam entre as opções.
 * @import import Radio from 'src/componentes/radio';
 */
function Radio({
  model,
  disabled,
  name,
  options,
  className,
  style,
  inline = true,
  inColum,
  colunaUnica = false,
  needId = false,
  showLabel = true,
  required = false,
  labelField,
  labelFunction,
  hintItens = null,
  hintLabel = 'label',
  hintKey = '',
  descriptionItems = null,
  optionSpacing = null,
  optionLabelStyle,
  onChange,
  getAlteracao,
}: RadioProps & WrappedComponentProps) {
  const isChecked = (item: RadioOption) => {
    if (model.value === null || model.value === undefined) return false;

    return needId
      ? String(item.id) === String(model.value)
      : item.id === (model.value as RadioOption).id;
  };

  const handleValueChange = (value: string) => {
    const item = (options as RadioOption[])[Number(value)];

    model.requestChange(needId ? item.id : item);
    onChange && onChange(item);

    if (getAlteracao != null) getAlteracao();
  };

  const getLabel = (item: RadioOption) => {
    if (labelFunction) return labelFunction(item);

    if (labelField) return item[labelField];

    return item;
  };

  const renderHint = (item: RadioOption) => {
    const hint = (hintItens as HintItem[]).find((i) => i.id == item.id);

    if (hint && hint[hintLabel]) {
      return (
        <Info
          style={{ alignSelf: 'flex-start' }}
          id={`hint-${hint.id}-${hintKey}`}
          placement="right"
          text={capitalizeFirstLetter(hint[hintLabel])}
        />
      );
    }
  };

  const renderDescription = (item: RadioOption) => {
    const description = (
      descriptionItems as { id: unknown; label?: string }[]
    ).find((i) => i.id === item.id);

    if (description && description.label) {
      return (
        <div className="tw:text-xs tw:leading-normal tw:text-zinc-400">
          {capitalizeFirstLetter(description.label)}
        </div>
      );
    }
    return null;
  };

  const checkedIndex = options ? options.findIndex(isChecked) : -1;
  const verticalSpacing = optionSpacing ?? (descriptionItems ? '12px' : '8px');

  // mb-3, form-check-inline e radio-option-* são ganchos do CSS das telas de
  // configuração (ConfigurationField.css, importacao-fields.css), que é da squad.
  return (
    <div className={cn('mb-3', className)} style={style}>
      <div className={cn(!!model.errors && 'tw:text-destructive')}>
        {showLabel && model.label && (
          <label className="tw:mb-2 tw:inline-block">
            {model.label} {required ? '*' : ''}
          </label>
        )}
        <RadioGroup
          className="radio-option-container tw:block"
          name={name}
          value={checkedIndex >= 0 ? String(checkedIndex) : ''}
          onValueChange={handleValueChange}
          disabled={disabled}
          aria-required={required}
          orientation={inline ? 'horizontal' : 'vertical'}
        >
          {options &&
            options.map((item, index) => (
              <div
                key={`div-options-${index}`}
                className={cn(
                  'radio-option-item',
                  inline &&
                    'form-check-inline tw:inline-flex tw:items-center tw:me-3',
                  inColum && 'tw:relative tw:px-3.75 tw:md:w-5/12',
                  inColum &&
                    colunaUnica &&
                    'tw:flex tw:flex-col tw:items-start tw:justify-center'
                )}
                style={inline ? undefined : { marginBottom: verticalSpacing }}
              >
                <div
                  className={cn(
                    'radio-option-content tw:flex',
                    descriptionItems
                      ? 'tw:flex-col tw:items-start'
                      : 'tw:items-center'
                  )}
                >
                  <div className="tw:flex tw:items-center">
                    <label
                      className={cn(
                        'radio-option-label tw:mb-2 tw:me-2 tw:flex tw:items-start tw:gap-1',
                        descriptionItems && 'tw:font-bold'
                      )}
                      style={optionLabelStyle}
                    >
                      <RadioGroupItem
                        value={String(index)}
                        disabled={disabled || item.disabled}
                        className="tw:mt-0.5"
                      />
                      {getLabel(item)}
                    </label>
                    {hintItens && renderHint(item)}
                  </div>
                  {descriptionItems && renderDescription(item)}
                </div>
              </div>
            ))}
        </RadioGroup>
        {model.erros && (
          <div className="tw:mt-1 tw:text-xs tw:text-destructive">
            {model.erros}
          </div>
        )}
      </div>
    </div>
  );
}

export default injectIntl(Radio);
